import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getElsLookups } from '../../../redux/actions/lookups/lookups';
import { getProfileOverview } from '../../../redux/actions/profile/profile';
import { getProfileDetails, resetGetProfileDetails } from '../../../redux/actions/profile/profileDetails';
import { setReadOnly } from '../../../redux/actions/readOnly/readOnly';
import { GetElsLookupsState } from '../../../redux/reducers/getLookups/getElsLookupsReducer';
import { ProfileDetailsState } from '../../../redux/reducers/getProfile/getProfileDetailsReducer';
import { ProfileOverviewState } from '../../../redux/reducers/getProfile/getProfileOverviewReducer';
import CampaignList from '../../../components/CampaignList/CampaignList';
import Button from '../../../components/common/Button/Button';
import LoadingSpinner from '../../../components/common/LoadingSpinner/LoadingSpinner';
import LandingBanner from '../../../components/LandingBanner/LandingBanner';
import Footer from '../../../layouts/Footer/Footer';
import Header from '../../../layouts/Header/Header';
import { isCampaignListDisplayed } from '../../../utils/campaignList';
import { formatRoll } from '../../../utils/formatter';
import "./Continue.css";
import { selectELSLookups, selectProfileGetDetails, selectProfileGetOverview } from '../../../redux/selectors/exportSelectorPaths';

export default function Continue(): JSX.Element {

    const { t } = useTranslation('transition');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isProfileDetailsReqSent, setIsProfileDetailsReqSent] = useState(false);

    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
    const profileDetails: ProfileDetailsState = useSelector(selectProfileGetDetails);
    const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups);
    const [continuing, setContinuing] = useState(false);
    const getProfileOverviewDispatched = useRef(false)
    const rollNumber = sessionStorage.getItem("rollNumber") as string
    const profileId = sessionStorage.getItem("profileId") as string
    const [address, setAddress] = useState("")

    useEffect(() => {
        dispatch(resetGetProfileDetails())
    }, [dispatch]);

    useEffect(() => {
        if (!getProfileOverviewDispatched.current &&
            !(profileOverview?.profile?.profile_id === Number(profileId))
        ) {
            dispatch(getProfileOverview(profileId, sessionStorage.getItem("wac") as string))
            getProfileOverviewDispatched.current = true
        }
    }, [dispatch, profileId, profileOverview])

    useEffect(() => {
        if (profileOverview.found && String(profileOverview.profile.profile_id) === sessionStorage.getItem("profileId")) {
            sessionStorage.removeItem('incomeReturnType'); // remove incomeReturnType for WIP as it's irrelevant
            sessionStorage.setItem("rollNumber", rollNumber)
            sessionStorage.setItem("profileId", String(profileOverview.profile.profile_id));
            sessionStorage.setItem("activeCampaignYear", String(profileOverview.profile.current_campaign.year));
            sessionStorage.setItem("campaignId", String(profileOverview.profile.current_campaign.campaignId));
            sessionStorage.setItem("campaignStatusCode", String(profileOverview.profile.current_campaign.statusCode));
            sessionStorage.setItem("currentSnapshotId", String(profileOverview.profile.current_campaign.currentSnapshotId));
            setAddress(profileOverview.profile.profile_address)
            if (getProfileOverviewDispatched.current) {
                getProfileOverviewDispatched.current = false
            }
        }
    }, [dispatch, profileOverview, rollNumber]);

    const continueReturn = (e: React.MouseEvent) => {
        e.preventDefault();
        if (sessionStorage.getItem("currentSnapshotId") !== null && sessionStorage.getItem("currentSnapshotId") !== 'undefined') {
            setIsProfileDetailsReqSent(true);
            dispatch(getProfileDetails(Number(sessionStorage.getItem("currentSnapshotId") as string)));
        }
        setContinuing(true);
        dispatch(setReadOnly(false))

        if (!elsLookupsState.success && profileOverview.profile.profile_type_code !== "GEV") { // disable ELS calls for GEV portfolio
            const propertyCode = sessionStorage.getItem("propertyCode") ?? ""
            dispatch(getElsLookups(propertyCode));
        }
    }

    useEffect(() => {
        if (isProfileDetailsReqSent && !profileDetails.loading && continuing) {
            // If profile type code is GEV, system doesn't check for ELS response success before navigating to portal
            // Check local storage is poulated before navigating to portal - bug fix for when system navigates to portal before local storage is populated
            const checkProfileDetailsInLocalStoage = (sessionStorage.getItem("rollNumber") && sessionStorage.getItem("profileId") && sessionStorage.getItem("activeCampaignYear")
                && sessionStorage.getItem("campaignId") && sessionStorage.getItem("campaignStatusCode") && sessionStorage.getItem("currentSnapshotId"));
            if ((profileOverview.profile.profile_type_code === "GEV" || elsLookupsState.success) && profileDetails.success && checkProfileDetailsInLocalStoage) {
                setContinuing(false);
                navigate('/portal')
            } else if (!profileDetails.success) {
                toast.error("Failed to retrieve profile details");
            }
        }
    }, [isProfileDetailsReqSent, profileDetails, elsLookupsState, continuing, navigate, profileOverview, rollNumber]);

    return (<>
        <Header showAutoSave={false} isInternalUser={false} />
        <LandingBanner />
        <LoadingSpinner loading={profileOverview.loading || profileDetails.loading || continuing}>
            <div className='continue-body-container'>
                <div className='continue-body'>
                    <h1>{t("continue.welcome")}!</h1>
                    <p>{`${t("continue.saved")}`}</p>
                    <div className='roll-info-container'>
                        <p className="strong roll-num-container">{formatRoll(rollNumber)}</p>
                        <p className="address-container">{address?.length > 260 ? address?.slice(0, 260) + "..." : address}</p>
                    </div>
                    <div className="button-group">
                        <Button onClick={e => continueReturn(e)} type="submit" text={t("continue.continue")} />
                        <Button onClick={() => navigate('/landing')} type="secondary" text={t("return")} />
                    </div>
                    {
                        isCampaignListDisplayed(profileOverview) &&
                        <div className="campaign-list-wrapper">
                            <div className="campaign-list">
                                <CampaignList />
                            </div>
                        </div>
                    }
                </div>
            </div>
        </LoadingSpinner>
        <div className="start-footer">
            <Footer />
        </div>
    </>);
}
